import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit, Inbox } from "lucide-react";

const inputCls = "w-full bg-background border border-input px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";

function NewsManager() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", category: "Update", image_url: "" });

  const load = () => api.get("/news").then((r) => setPosts(r.data));

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ title: "", excerpt: "", body: "", category: "Update", image_url: "" });
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ title: p.title, excerpt: p.excerpt, body: p.body, category: p.category, image_url: p.image_url || "" });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/news", form);
        toast.success("Post created.");
      } else {
        await api.put(`/news/${editing}`, form);
        toast.success("Post updated.");
      }
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed.");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/news/${id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl font-bold text-primary">News & Updates</h2>
        {!editing && (
          <button onClick={startNew} className="bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:bg-[#072448]" data-testid="admin-new-post">
            <Plus size={16} /> New Post
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={save} className="bg-card border border-border p-6 space-y-4 mb-8" data-testid="admin-post-form">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Title</span>
            <input required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Category</span>
              <input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Image URL</span>
              <input className={inputCls} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
            </label>
          </div>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Excerpt</span>
            <textarea rows="2" required className={inputCls} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Body</span>
            <textarea rows="8" required className={inputCls} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </label>
          <div className="flex gap-3">
            <button type="submit" data-testid="admin-post-save" className="bg-accent text-accent-foreground px-6 py-2.5 font-bold uppercase tracking-wider text-sm hover:bg-[#8A1333]">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="border border-border px-6 py-2.5 font-bold uppercase tracking-wider text-sm">Cancel</button>
          </div>
        </form>
      ) : null}

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="bg-card border border-border p-5 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-accent">{p.category}</div>
              <h3 className="font-serif text-lg font-bold text-primary mt-1">{p.title}</h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.excerpt}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => startEdit(p)} className="p-2 border border-border hover:border-primary" data-testid={`edit-${p.id}`}><Edit size={16} /></button>
              <button onClick={() => del(p.id)} className="p-2 border border-border hover:border-accent text-accent" data-testid={`delete-${p.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubmissionsViewer() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("volunteers");

  useEffect(() => {
    api.get("/admin/submissions").then((r) => setData(r.data));
  }, []);

  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  const tabs = [
    { key: "volunteers", label: `Volunteers (${data.volunteers.length})` },
    { key: "yard_signs", label: `Yard Signs (${data.yard_signs.length})` },
    { key: "pledges", label: `Pledges (${data.pledges.length})` },
    { key: "contacts", label: `Contacts (${data.contacts.length})` },
  ];

  return (
    <div>
      <h2 className="font-serif text-2xl font-bold text-primary mb-6">Submissions</h2>
      <div className="flex flex-wrap gap-0 border border-border mb-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 min-w-[140px] py-3 text-xs font-bold uppercase tracking-wider border-l border-border first:border-l-0 ${
              tab === t.key ? "bg-primary text-primary-foreground" : "bg-card text-primary hover:bg-secondary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {data[tab].length === 0 ? (
        <div className="bg-card border border-border p-10 text-center text-muted-foreground flex flex-col items-center gap-3">
          <Inbox size={32} />
          <p>No submissions yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data[tab].map((row) => (
            <div key={row.id} className="bg-card border border-border p-5">
              <div className="grid md:grid-cols-3 gap-2 text-sm">
                <div><strong className="text-primary">{row.name}</strong></div>
                <div className="text-muted-foreground">{row.email}</div>
                <div className="text-muted-foreground text-xs">{new Date(row.created_at).toLocaleString()}</div>
              </div>
              {tab === "pledges" && (
                <div className="mt-2 text-sm">Amount: <strong className="text-accent">${row.amount}</strong> · {row.employer || "—"} · {row.occupation || "—"}</div>
              )}
              {tab === "yard_signs" && (
                <div className="mt-2 text-sm text-muted-foreground">{row.address}, {row.city} {row.zip_code}</div>
              )}
              {tab === "volunteers" && row.interests?.length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground">Interests: {row.interests.join(", ")}</div>
              )}
              {(row.message || row.subject) && (
                <div className="mt-2 text-sm text-foreground/80">{row.subject ? <strong>{row.subject}: </strong> : null}{row.message}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("news");

  if (loading) return <div className="p-20 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10 pb-6 border-b border-border">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="flag-rule"><span /><span /><span /></div>
              <span className="text-xs uppercase tracking-[0.22em] font-bold text-accent">Admin Console</span>
            </div>
            <h1 className="font-serif text-4xl font-black text-primary">Welcome, {user.name}</h1>
          </div>
          <button onClick={handleLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-border px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:border-accent hover:text-accent transition-colors">
            <LogOut size={16} /> Sign Out
          </button>
        </div>

        <div className="flex gap-0 border border-border mb-8 max-w-md">
          <button
            onClick={() => setView("news")}
            data-testid="admin-tab-news"
            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider ${view === "news" ? "bg-primary text-primary-foreground" : "bg-card text-primary"}`}
          >
            News
          </button>
          <button
            onClick={() => setView("submissions")}
            data-testid="admin-tab-submissions"
            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider border-l border-border ${view === "submissions" ? "bg-primary text-primary-foreground" : "bg-card text-primary"}`}
          >
            Submissions
          </button>
        </div>

        {view === "news" ? <NewsManager /> : <SubmissionsViewer />}
      </div>
    </div>
  );
}
